/**
 * Run an async task over each item with a bounded number in flight at once.
 *
 * Uploads fan out one request per file, but firing them all at once saturates
 * the browser's per-origin connection cap AND the Corrigo upload endpoint behind
 * the server — a burst that pushes attempts past the server's per-request timeout,
 * triggering retries that pile on yet more load (a thundering herd). Capping the
 * pool keeps the load steady so each request finishes well inside the timeout.
 *
 * Mirrors `Promise.allSettled` semantics: every task runs, a rejection never
 * aborts the others, and results come back in input order as settled results.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results = new Array<PromiseSettledResult<R>>(items.length);
  let next = 0;

  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const index = next++;
      try {
        results[index] = { status: 'fulfilled', value: await task(items[index], index) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  };

  const workerCount = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

/**
 * Max simultaneous file uploads. Kept below the browser's ~6-per-origin
 * connection cap so the Corrigo upload endpoint behind the server isn't
 * overwhelmed by a burst.
 */
export const UPLOAD_CONCURRENCY = 4;
